import {useCallback, useState} from 'react';

import {TEntityValue} from 'modules/data/data-types';
import {getFinalStatus} from 'store/slices/documents-uploading-slice';

/**
 * Интерфейс для параметров хука useFinalStatus
 * @interface
 * @property {Record<string, EntityValue>[]} selectedDocuments - Массив выбранных документов
 * @property {string | undefined} actionCode - Код действия
 */
interface IUseFinalStatusArgs {
    selectedDocuments: Record<string, TEntityValue>[];
    actionCode: string | undefined;
    entityName: string;
}

/**
 * Хук для получения финального статуса документа
 *
 * @param {IUseFinalStatusArgs} props - Параметры хука
 * @returns {Object} Возвращает объект с финальным статусом и функциями для управления им
 * @returns {string | null} finalStatus - Финальный статус документа
 * @returns {Function} fetchFinalStatus - Функция для получения финального статуса
 * @returns {Function} setFinalStatus - Функция для установки финального статуса
 */
export const useFinalStatus = ({selectedDocuments, actionCode, entityName}: IUseFinalStatusArgs) => {
    const [finalStatus, setFinalStatus] = useState<string | null>(null);

    const fetchFinalStatus = useCallback(async () => {
        if (selectedDocuments.length !== 1 || !actionCode) {
            return;
        }

        try {
            const res = await getFinalStatus(
                selectedDocuments[0]?.docStatusCode as string,
                actionCode,
                `app.${entityName}`,
            );
            if (res.status === 200 && res.data) {
                setFinalStatus(res.data);
            }
        } catch (error) {
            console.error(error);
        }
    }, [selectedDocuments, actionCode]);

    return {
        finalStatus,
        fetchFinalStatus,
        setFinalStatus,
    };
};
